import React, { useEffect, useState } from 'react';
import { Button, buttonVariants } from './ui/button';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { cn } from '../lib/utils';

export function Contract({ contract, contractError, contractGenerated, generateContract, clearContract, outputLabel = 'contract' }) {
  const [loading, setLoading] = useState(false);

  // The result arriving (success or error) always stops the spinner. onClick
  // resets any previous result first, so this reliably fires even when
  // regenerating — including when the regenerated file is byte-identical.
  useEffect(() => {
    if (contractGenerated) {
      setLoading(false);
    }
  }, [contractGenerated]);

  const onClick = (e) => {
    setLoading(true);
    if (clearContract) clearContract();
    generateContract(e);
  };

  const label = outputLabel || 'contract';
  const titleLabel = label.replace(/\b\w/g, (c) => c.toUpperCase());
  const fileName =
    label.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') + '.docx';

  const href =
    'data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,' +
    encodeURIComponent(contract);

  const succeeded = contractGenerated && !contractError;

  return (
    <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className={cn('theEnd space-y-3 text-center', !succeeded && 'generate')}>
        <h2 className="font-display text-3xl font-medium tracking-tight text-primary-900">Congratulations</h2>
        <p className="text-gray-500">You have reached the end of the data collection form.</p>
        <p className="text-gray-500">
          {succeeded
            ? `Your ${label} is ready to download.`
            : `You can now generate the ${label} by clicking on the button below.`}
        </p>

        {/* Only one filled CTA is ever on screen: before generation it is
            "Generate", afterwards it becomes "Download" and regenerating
            steps back to a secondary action. */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
          {succeeded && (
            <a href={href} download={fileName} className={cn(buttonVariants())}>
              Download
            </a>
          )}
          <Button
            id="generate-contract"
            variant={succeeded ? 'outline' : 'default'}
            loading={loading}
            onClick={onClick}
          >
            {succeeded ? 'Regenerate' : `Generate ${titleLabel}`}
          </Button>
        </div>
      </div>

      {contractGenerated && contractError && (
        <div className="theEnd error">
          <Alert variant="destructive">
            <AlertTitle>We're sorry. Something went wrong.</AlertTitle>
            <AlertDescription>
              <p>An error occurred during the contract generation.</p>
              <p className="font-mono text-sm mt-1">{contractError}</p>
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
}
